// MUI theme — single source of truth for design tokens.
// Values are literals, not CSS var() references — MUI computes hover/disabled
// overlays from real colors. spacing: 4 → theme.spacing(n) = n×4px.
// Keep palette/shape in sync with src/styles/theme.css until CSS migration completes.
import { createTheme } from '@mui/material/styles';
import {
  COLOR_SECONDARY_BTN,
  COLOR_SECONDARY_BTN_TEXT,
  COLOR_SECONDARY_BTN_HOVER,
  COLOR_SECONDARY_BTN_ACTIVE,
  COLOR_SECONDARY_BTN_DISABLED,
  COLOR_SECONDARY_BTN_DISABLED_TEXT,
  containedSecondaryOverrides,
} from './secondaryButtonTokens.js';

// Field-notebook: warm paper, ink black, marigold accent.
const COLOR_PRIMARY = '#8A5A12';
/** Accent / checkbox fill — not the contained-button fill. */
const COLOR_SECONDARY = '#B87F1E';
/** Darker ochre for ErrorBoundary / actionIconButtonSx (`secondary.dark`). */
const COLOR_SECONDARY_DARK = '#9C6B18';
const COLOR_TEXT_PRIMARY = '#23241F';
const COLOR_TEXT_SUBTLE = '#6B6759';
const COLOR_TEXT_DISABLED = '#9C9686';
const COLOR_SEMANTIC_WARNING_TEXT = '#5C3B0C';
/** Light amber caption ink on dark row tints (theme.css dark prefers-color-scheme). */
const COLOR_SEMANTIC_WARNING_TEXT_DARK = '#E8C88A';
/** Quiet warm tint for Te plannen parents with some (not all) leaves scheduled. */
const COLOR_ROW_PARTIALLY_SCHEDULED = '#F5EBD8';
/** Dark-scheme companion tint — calm, still ≥4.5:1 with light text. */
const COLOR_ROW_PARTIALLY_SCHEDULED_DARK = '#2A2418';

export {
  COLOR_SEMANTIC_WARNING_TEXT,
  COLOR_SEMANTIC_WARNING_TEXT_DARK,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SUBTLE,
  COLOR_ROW_PARTIALLY_SCHEDULED,
  COLOR_ROW_PARTIALLY_SCHEDULED_DARK,
  COLOR_SECONDARY_BTN,
  COLOR_SECONDARY_BTN_TEXT,
  COLOR_SECONDARY_BTN_HOVER,
  COLOR_SECONDARY_BTN_ACTIVE,
  COLOR_SECONDARY_BTN_DISABLED,
  COLOR_SECONDARY_BTN_DISABLED_TEXT,
};

/** Shared keyboard focus ring — marigold primary, no browser-default blue. */
const focusVisibleRing = {
  '&:focus': {
    outlineColor: COLOR_PRIMARY,
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineOffset: '2px',
    outlineColor: COLOR_PRIMARY,
  },
};

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: '#F7F5F0', paper: '#FFFEFB', subtle: '#EDE9E0' },
    text: {
      primary: COLOR_TEXT_PRIMARY,
      secondary: '#6B6759',
      disabled: COLOR_TEXT_DISABLED,
      subtle: COLOR_TEXT_SUBTLE,
    },
    grey: { 100: '#F7F5F0', 200: '#EDE9E0', 300: '#DED7C8' },
    neutral: {
      border: '#DED7C8',
      icon: '#8A8471',
      dark: '#6B6759',
      dot: '#9C9686',
      chipBg: '#EDE9E0',
      doneBg: '#F0ECE2',
    },
    divider: '#DED7C8',
    primary: { main: COLOR_PRIMARY },
    secondary: {
      main: COLOR_SECONDARY,
      dark: COLOR_SECONDARY_DARK,
      contrastText: COLOR_SECONDARY_BTN_TEXT,
    },
    error: { main: '#B5473A' },
    warning: { main: '#D9A441' },
    success: { main: '#4B7B5E' },
    info: { main: '#4A4638' },
  },
  shape: { borderRadius: 4 },
  spacing: 4,
  typography: {
    fontFamily: '"Public Sans", "Segoe UI", arial, sans-serif',
    fontSize: 14,
  },
  components: {
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: focusVisibleRing,
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: ({ theme: t }) => ({
        root: { border: `1px solid ${t.palette.divider}` },
      }),
    },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          ...focusVisibleRing,
        },
        containedSecondary: containedSecondaryOverrides,
      },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiIconButton: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: focusVisibleRing,
      },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: ({ theme: t }) => ({
        root: {
          height: 18,
          fontSize: '0.72em',
          fontWeight: 400,
          backgroundColor: t.palette.grey[200],
          color: t.palette.text.subtle,
        },
      }),
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: COLOR_PRIMARY },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: COLOR_TEXT_SUBTLE,
          '&.Mui-selected': { color: COLOR_PRIMARY, fontWeight: 600 },
          '&:active': { backgroundColor: 'transparent' },
          ...focusVisibleRing,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: focusVisibleRing,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: focusVisibleRing,
      },
    },
    MuiLink: {
      styleOverrides: {
        root: focusVisibleRing,
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: COLOR_TEXT_DISABLED,
          '&.Mui-checked': { color: COLOR_SECONDARY },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: COLOR_TEXT_SUBTLE,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLOR_PRIMARY,
          },
          ...focusVisibleRing,
        },
        input: {
          '&:focus': { outline: 'none' },
          '&::placeholder': {
            color: COLOR_TEXT_SUBTLE,
            opacity: 1,
          },
        },
      },
    },
  },
});

export default theme;
